import { useRef } from 'react'
import { FetchQueryRequest, StoredFilterParams } from '@equipmentshare/dcf-search-filter-experience'

const DEFAULT_PAGE_SIZE = 25

export function readSfeParam<Req extends FetchQueryRequest>( params: StoredFilterParams<Req>, key: keyof Req ): string | undefined {
	const value = params[ key ]
	return Array.isArray( value ) ? value[ 0 ] : value
}

export function readSfeListParam<Req extends FetchQueryRequest>( params: StoredFilterParams<Req>, key: keyof Req ): string[] | undefined {
	const value = params[ key ]
	if ( value === undefined ) {
		return undefined
	}
	return Array.isArray( value ) ? value : [ value ]
}

export function readSfePaging<Req extends FetchQueryRequest & { page?: number, size?: number }>(
	params: StoredFilterParams<Req>,
	defaultSize = DEFAULT_PAGE_SIZE
) {
	// Parsed the way SFE parses them, so a malformed URL falls back to the same page and size its grid shows.
	const page = parseInt( readSfeParam( params, 'page' ) ?? '', 10 )
	const size = parseInt( readSfeParam( params, 'size' ) ?? '', 10 )
	return {
		// SFE's `page` param is 1-based; every helper here works 0-based.
		page: page > 0 ? page - 1 : 0,
		size: size > 0 ? size : defaultSize,
	}
}

// No inventory-api connection the V* views use exposes a real total, so grids page
// "is there a next page" style until OpenSearch (docs/list-view-search-migration.md).
export function estimateRowCount( page: number, size: number, rowsOnPage: number, hasNextPage: boolean ) {
	return page * size + rowsOnPage + ( hasNextPage ? 1 : 0 )
}

/**
 * Cursor connections can only page forward from a known cursor, so the end cursor
 * of every page seen is kept, keyed by page. `resetKey` should cover everything
 * that changes the result set (search, filters, page size); a change starts over.
 */
export function useCursorPaging( page: number, resetKey: string ) {
	const cursorsRef = useRef<( string | undefined )[]>( [ undefined ] )
	const resetKeyRef = useRef( resetKey )

	if ( resetKeyRef.current !== resetKey ) {
		cursorsRef.current = [ undefined ]
		resetKeyRef.current = resetKey
	}

	return {
		after: cursorsRef.current[ page ],
		// Pass the query's own `data`, never `previousData`: while this page loads,
		// `previousData` holds the last page's cursor and would be filed under this one.
		recordEndCursor: ( endCursor: string | null | undefined ) => {
			if ( endCursor ) {
				cursorsRef.current[ page + 1 ] = endCursor
			}
		},
	}
}

// Offset queries return a bare list with no `hasNextPage`, so fetch one extra row
// and use its presence as the answer.
export function offsetPaging( page: number, size: number ) {
	return { offset: page * size, size: size + 1 }
}

export function trimOffsetPage<Row>( rows: Row[], size: number ) {
	return { rows: rows.slice( 0, size ), hasNextPage: rows.length > size }
}
